import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import PasswordInput from "../components/PasswordInput";
import { FileSpreadsheet, Lock, Unlock, Plus, Trash2, Search, Users, Package, Sparkles } from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const PLANO_LABELS_COT = { trial: "Trial 14d", start: "Start", pro: "Pro", premium: "Premium", corporativo: "Corporativo" };
const PLANO_COLORS_COT = { trial: "bg-amber-100 text-amber-800", start: "bg-slate-100 text-slate-700", pro: "bg-blue-100 text-blue-700", premium: "bg-emerald-100 text-emerald-700", corporativo: "bg-purple-100 text-purple-700" };

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; } };

export default function GestaoCotacaoAssinantesPage() {
  const [assinantes, setAssinantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", empresa: "", telefone: "", plano: "trial" });
  const [creating, setCreating] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockMotivo, setBlockMotivo] = useState("");
  const [blockSaving, setBlockSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/super-admin/cotacao/assinantes`);
      setAssinantes(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const criar = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/super-admin/cotacao/assinantes`, form);
      toast.success("Assinante criado");
      setCreateOpen(false);
      setForm({ nome: "", email: "", senha: "", empresa: "", telefone: "", plano: "trial" });
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar");
    } finally { setCreating(false); }
  };

  const bloquear = async () => {
    setBlockSaving(true);
    try {
      await axios.put(`${API}/super-admin/cotacao/assinantes/${blockTarget.id}/status`, {
        bloqueado: !blockTarget.bloqueado, motivo: blockMotivo,
      });
      toast.success(blockTarget.bloqueado ? "Desbloqueado" : "Bloqueado");
      setBlockOpen(false); setBlockTarget(null); setBlockMotivo("");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally { setBlockSaving(false); }
  };

  const trocarPlano = async (assinante, novoPlano) => {
    try {
      await axios.put(`${API}/super-admin/cotacao/assinantes/${assinante.id}/plano`, { plano: novoPlano });
      toast.success("Plano atualizado");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const excluir = async () => {
    if (delConfirm !== "EXCLUIR") { toast.error("Digite EXCLUIR para confirmar"); return; }
    try {
      await axios.delete(`${API}/super-admin/cotacao/assinantes/${delTarget.id}`);
      toast.success("Assinante excluído");
      setDelOpen(false); setDelTarget(null); setDelConfirm("");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const filtrados = assinantes.filter((a) => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return (a.nome || "").toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q) || (a.empresa || "").toLowerCase().includes(q);
  });

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="gestao-cotacao-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-blue-700" />
            Assinantes — Sistema de Cotações
          </h1>
          <p className="text-sm text-slate-500 mt-1">Base separada do Compromisso Digital. Total: {assinantes.length}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-novo-assinante-cot" className="bg-blue-700 hover:bg-blue-800">
          <Plus className="w-4 h-4 mr-1" /> Novo assinante
        </Button>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input placeholder="Buscar por nome, email ou empresa..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="busca-cot" />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : filtrados.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum assinante encontrado.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtrados.map((a) => (
            <Card key={a.id} className={a.bloqueado ? "border-red-200" : ""} data-testid={`assinante-cot-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold">{a.nome}</div>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${PLANO_COLORS_COT[a.plano] || "bg-slate-100"}`}>
                        {PLANO_LABELS_COT[a.plano] || a.plano}
                      </span>
                      {a.bloqueado && <span className="px-2 py-0.5 rounded text-[11px] bg-red-100 text-red-700 font-semibold">BLOQUEADO</span>}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">{a.email}</div>
                    {a.empresa && <div className="text-xs text-slate-500">{a.empresa}</div>}
                    <div className="text-xs text-slate-500 mt-1">
                      Cadastrado em {fmtDate(a.created_at)}
                      {a.trial_ate && <> · Trial até {fmtDate(a.trial_ate)}</>}
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-slate-600">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {a.stats?.fornecedores || 0} fornecedores</span>
                      <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {a.stats?.produtos || 0} produtos</span>
                      <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> {a.stats?.cotacoes || 0} cotações</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={a.plano || "trial"}
                      onChange={(e) => trocarPlano(a, e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                      data-testid={`plano-select-${a.id}`}
                    >
                      {Object.entries(PLANO_LABELS_COT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <Button
                      size="sm"
                      variant={a.bloqueado ? "default" : "outline"}
                      onClick={() => { setBlockTarget(a); setBlockOpen(true); setBlockMotivo(""); }}
                      data-testid={`btn-bloq-${a.id}`}
                    >
                      {a.bloqueado ? <><Unlock className="w-4 h-4 mr-1" />Desbloquear</> : <><Lock className="w-4 h-4 mr-1" />Bloquear</>}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => { setDelTarget(a); setDelOpen(true); setDelConfirm(""); }} data-testid={`btn-excluir-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Criar */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dialog-criar-cot">
          <DialogHeader><DialogTitle>Novo assinante — Cotações</DialogTitle><DialogDescription>Crie manualmente um assinante do Sistema de Cotações.</DialogDescription></DialogHeader>
          <form onSubmit={criar} className="space-y-3">
            <div><Label>Nome*</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div><Label>Empresa</Label><Input value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} /></div>
            <div><Label>Email*</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefone</Label><Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
            <div><Label>Senha*</Label><PasswordInput required value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} /></div>
            <div>
              <Label>Plano</Label>
              <select value={form.plano} onChange={(e) => setForm({ ...form, plano: e.target.value })} className="w-full border rounded px-3 py-2">
                {Object.entries(PLANO_LABELS_COT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating} className="bg-blue-700 hover:bg-blue-800">{creating ? "Criando..." : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bloquear */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{blockTarget?.bloqueado ? "Desbloquear" : "Bloquear"} assinante</DialogTitle><DialogDescription>Altere o status de acesso deste assinante.</DialogDescription></DialogHeader>
          <div>
            <div className="text-sm">{blockTarget?.nome} — {blockTarget?.email}</div>
            {!blockTarget?.bloqueado && (
              <div className="mt-3"><Label>Motivo (opcional)</Label><Input value={blockMotivo} onChange={(e) => setBlockMotivo(e.target.value)} /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)}>Cancelar</Button>
            <Button onClick={bloquear} disabled={blockSaving}>{blockSaving ? "Salvando..." : "Confirmar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excluir */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Excluir assinante</DialogTitle><DialogDescription>Ação irreversível — todos os dados do assinante serão apagados.</DialogDescription></DialogHeader>
          <div className="text-sm">
            Todos os dados de <b>{delTarget?.email}</b> (fornecedores, produtos, cotações) serão removidos permanentemente.
            <div className="mt-3"><Label>Digite EXCLUIR para confirmar</Label>
              <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} data-testid="del-confirm-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={excluir}>Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
